import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import type {
  CliLibraryAdaptationPreflightData,
  LibraryEvidenceRef,
  LibraryEvidenceSelection,
  LibraryVariableResolution,
} from '@modules/shared-types';
import { useProjectDefaultAIModel } from '@modules/ai-settings';

import {
  LibraryAdaptationApiError,
  LibraryAdaptationService,
} from '../services/LibraryAdaptationService';
import { readLibraryAdaptationState } from '../utils/libraryAdaptationState';

export interface UseLibraryAdaptationInspectorOptions {
  projectId: string;
  strategyId: string;
  block: StrategyBlock;
  contextRevision?: string | number;
  onTaskStarted?: (taskId: string, blockId: string) => void;
}

export function useLibraryAdaptationInspector({
  projectId,
  strategyId,
  block,
  contextRevision,
  onTaskStarted,
}: UseLibraryAdaptationInspectorOptions) {
  const persisted = readLibraryAdaptationState(block);
  const service = useMemo(
    () => new LibraryAdaptationService(projectId, strategyId),
    [projectId, strategyId],
  );
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [model, setModel] = useState(projectDefaultModel.value);
  const [selection, setSelection] = useState<LibraryEvidenceSelection>(
    persisted?.evidence ?? { mode: 'strategy-default' },
  );
  const [operatorInstruction, setOperatorInstruction] = useState('');
  const [variableResolutions, setVariableResolutions] = useState<Record<string, LibraryVariableResolution>>(
    persisted?.variableResolutions ?? {},
  );
  const [preflight, setPreflight] = useState<CliLibraryAdaptationPreflightData | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [adaptLoading, setAdaptLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);

  useEffect(() => {
    setSelection(persisted?.evidence ?? { mode: 'strategy-default' });
    setOperatorInstruction('');
    setVariableResolutions(persisted?.variableResolutions ?? {});
    setPreflight(null);
    setError(null);
  }, [block.id]);

  useEffect(() => {
    if (!projectDefaultModel.loading) setModel(projectDefaultModel.value);
  }, [projectDefaultModel.loading, projectDefaultModel.value]);

  const refreshPreflight = useCallback(async (
    nextSelection: LibraryEvidenceSelection = selection,
    nextVariableResolutions: Record<string, LibraryVariableResolution> = variableResolutions,
  ): Promise<CliLibraryAdaptationPreflightData | null> => {
    if (!block.id) return null;
    if (nextSelection.mode === 'selected-only' && nextSelection.refs.length === 0) {
      setError('Select at least one available evidence item, or choose No evidence.');
      return null;
    }
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    setPreflightLoading(true);
    setError(null);
    try {
      const result = await service.preflight(block.id, {
        evidence: nextSelection,
        variableResolutions: nextVariableResolutions,
        ...(operatorInstruction.trim() ? { operatorInstruction: operatorInstruction.trim() } : {}),
      });
      if (requestSequence.current === sequence) setPreflight(result);
      return result;
    } catch (cause) {
      if (requestSequence.current === sequence) {
        setError(cause instanceof Error ? cause.message : 'Could not resolve library adaptation context.');
      }
      return null;
    } finally {
      if (requestSequence.current === sequence) setPreflightLoading(false);
    }
  }, [block.id, operatorInstruction, selection, service, variableResolutions]);

  useEffect(() => {
    if (!block.id) return;
    void refreshPreflight(selection);
    // Block identity is the initial boundary. The canvas increments the
    // revision only after persisted graph/strategy context changes complete,
    // so the visible preview stays aligned with the server fingerprint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, contextRevision]);

  const setEvidenceMode = useCallback((mode: LibraryEvidenceSelection['mode']) => {
    const next: LibraryEvidenceSelection = mode === 'selected-only'
      ? { mode, refs: selection.mode === 'selected-only' ? selection.refs : [] }
      : { mode };
    setSelection(next);
    if (next.mode === 'selected-only' && next.refs.length === 0) {
      setError('Choose one or more evidence items below. Nothing is silently widened.');
      return;
    }
    void refreshPreflight(next);
  }, [refreshPreflight, selection]);

  const toggleEvidence = useCallback((ref: LibraryEvidenceRef) => {
    const current = selection.mode === 'selected-only' ? selection.refs : [];
    const key = `${ref.kind}:${ref.id}`;
    const exists = current.some((item) => `${item.kind}:${item.id}` === key);
    const refs = exists
      ? current.filter((item) => `${item.kind}:${item.id}` !== key)
      : [...current, ref];
    const next: LibraryEvidenceSelection = { mode: 'selected-only', refs };
    setSelection(next);
    if (refs.length === 0) {
      setError('Selected only requires at least one available item.');
      return;
    }
    void refreshPreflight(next);
  }, [refreshPreflight, selection]);

  const setVariableResolution = useCallback((
    sourceVariableId: string,
    resolution: LibraryVariableResolution,
  ) => {
    const next = { ...variableResolutions, [sourceVariableId]: resolution };
    setVariableResolutions(next);
    void refreshPreflight(selection, next);
  }, [refreshPreflight, selection, variableResolutions]);

  const startAdaptation = useCallback(async () => {
    if (!block.id || !model || projectDefaultModel.loading) return null;
    setAdaptLoading(true);
    setError(null);
    try {
      const reviewed = await refreshPreflight(selection);
      if (!reviewed) return null;
      const result = await service.adapt(block.id, {
        evidence: selection,
        variableResolutions,
        ...(operatorInstruction.trim() ? { operatorInstruction: operatorInstruction.trim() } : {}),
        model,
        idempotencyKey: globalThis.crypto?.randomUUID?.()
          ?? `library-adapt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        expectedContextFingerprint: reviewed.contextFingerprint,
      });
      setPreflight(result);
      onTaskStarted?.(result.taskId, block.id);
      return result;
    } catch (cause) {
      const message = cause instanceof Error
        ? cause.message
        : 'Could not start library adaptation.';
      if (cause instanceof LibraryAdaptationApiError && cause.status === 409) {
        await refreshPreflight(selection);
      }
      setError(message);
      return null;
    } finally {
      setAdaptLoading(false);
    }
  }, [
    block.id,
    model,
    onTaskStarted,
    operatorInstruction,
    projectDefaultModel.loading,
    refreshPreflight,
    selection,
    service,
    variableResolutions,
  ]);

  return {
    persisted,
    selection,
    setEvidenceMode,
    toggleEvidence,
    variableResolutions,
    setVariableResolution,
    operatorInstruction,
    setOperatorInstruction,
    model,
    setModel,
    modelLoading: projectDefaultModel.loading,
    preflight,
    preflightLoading,
    adaptLoading,
    error,
    refreshPreflight,
    startAdaptation,
  };
}
